import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  QuestionMarkCircleIcon,
  VideoCameraIcon,
  BookOpenIcon,
} from "../../utils/icons";

const features = [
  {
    icon: QuestionMarkCircleIcon,
    title: "Personalized Hair Analysis",
    description:
      "Get expert recommendations based on your unique hair profile and needs",
    link: "/hair-quiz",
    color: "from-gold-400/20 to-transparent",
    delay: 0.2,
  },
  {
    icon: VideoCameraIcon,
    title: "Virtual Consultations",
    description: "One-on-one sessions with our certified hair care specialists",
    link: "/consultation",
    color: "from-primary-400/20 to-transparent",
    delay: 0.4,
  },
  {
    icon: BookOpenIcon,
    title: "Hair Journey Tracking",
    description:
      "Document and monitor your progress with our digital hair diary",
    link: "/hair-journal",
    color: "from-accent-400/20 to-transparent",
    delay: 0.6,
  },
];

export default function Features() {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#DB5461]/5 via-white to-white" />
      <motion.div style={{ y }} className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#DB5461]/20 rounded-full mix-blend-multiply filter blur-3xl" />
        <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-primary-200/30 rounded-full mix-blend-multiply filter blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#DB5461]/20 rounded-full mix-blend-multiply filter blur-3xl" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium tracking-wider text-gold-500 bg-gold-50 rounded-full">
            EXCLUSIVE FEATURES
          </span>
          <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
            Elevate Your Hair Care Journey
          </h2>
          <div className="w-24 h-1 bg-[#DB5461] mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: feature.delay }}
            >
              <Link to={feature.link}>
                <motion.div
                  whileHover={{ y: -5, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative group h-full bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300"
                >
                  {/* Decorative gradient */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Icon */}
                  <div className="relative mb-6">
                    <div
                      className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center`}
                    >
                      <feature.icon className="w-8 h-8 text-primary-900" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative space-y-4">
                    <h3 className="text-xl font-serif font-bold text-primary-900 group-hover:text-[#DB5461] transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>

                    {/* Arrow indicator */}
                    <div className="flex items-center text-[#DB5461] font-medium group-hover:text-[#DB5461]/80 transition-colors duration-300">
                      <span>Get Started</span>
                      <motion.svg
                        initial={{ x: 0 }}
                        animate={{ x: [0, 5, 0] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="ml-2 w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </motion.svg>
                    </div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
